/**
 * The form document, exactly as the server stores it in OBJECT_REVISION.SCHEMA.
 *
 * ONE SHAPE OF DATA, not a designer-private model that gets translated on save. A translation layer
 * is where a designer starts believing something the server does not, and the server is the one that
 * decides -- it overwrites what a placed field IS from the shape on every save.
 */

/** A named group of placements. Keyed on `key`, NOT `code` -- the renderer looks it up by key. */
export type Section = {
    key: string;
    title?: string;
    description?: string | null;
    /** Which tab this section sits under. Absent means outside the tabs, and always shown. */
    tab?: string | null;
};

/**
 * A named group of SECTIONS. Tabs never hold fields directly — a field is placed in a section, and
 * the section says which tab it appears under, so adding tabs to an existing form is one property
 * per section rather than a re-layout.
 */
export type Tab = {
    key: string;
    title?: string;
    order?: number;
};

/** A field placed on the canvas. Layout is the designer's; the rest is the shape's. */
export type Placement = {
    code: string;
    /** The designer's half. */
    section?: string | null;
    order?: number;
    width?: "full" | "half" | "third" | "two-thirds";
    label?: string | null;
    help?: string | null;
    placeholder?: string | null;
    readOnly?: boolean;
    /** Offered as a filter in the list's Filter by pane. Only a bounded control can be: a facet is
     *  the values held with a count against each, which is useful for five departments and useless
     *  for free text. The server refuses it on anything else. */
    filterable?: boolean;
    control?: string | null;
    /** The shape's half — sent back untouched, overwritten server-side for a declared code. */
    name?: string;
    dataTypeRevisionUSID?: string | null;
    description?: string | null;
    retired?: boolean;
    requiredWhen?: any;
    /** Set by the server for a code the shape does not declare. Adopted onto the shape at Release. */
    new?: boolean;
    /**
     * When to DRAW this field. Same conditions as required-ness and the same evaluator, so the two
     * cannot disagree. Presentation only: a hidden field is still required if its rule says so.
     */
    showWhen?: Condition[] | null;
    /** A RECORD's placement only: keep this record's own definition of a declared field through a
     *  save, instead of re-reading it from the shape. The owner's choice, per field. */
    keepOwn?: boolean;
};

/** One condition over a sibling field's value. Shared by requiredWhen and showWhen. */
export type Condition = {
    field: string;
    operator: "ANSWERED" | "NOT ANSWERED" | "EQUAL" | "NOT EQUAL" | "IN";
    value?: any;
};

export const SHOW_WHEN_OPERATORS: Condition["operator"][] =
    ["EQUAL", "NOT EQUAL", "IN", "ANSWERED", "NOT ANSWERED"];

/** Which operators take a value at all. ANSWERED and NOT ANSWERED are about presence. */
export const OPERATORS_TAKING_A_VALUE = new Set(["EQUAL", "NOT EQUAL", "IN"]);

/** A widget that carries no data. A palette of TYPES cannot express a heading, which is why the
 *  palette is widgets. */
export type DisplayItem = {
    kind: "heading" | "instructions" | "divider";
    text?: string | null;
    section?: string | null;
    order?: number;
};

export type FormDefinition = {
    recordTypeRevisionUSID?: string | null;
    tabs?: Tab[];
    sections?: Section[];
    fields?: Placement[];
    display?: DisplayItem[];
};

/** A field the SHAPE declares — the palette's contents. */
export type ShapeField = {
    Code: string;
    Name: string;
    Description?: string | null;
    DataTypeRevisionUSID?: string | null;
    TypeName?: string | null;
    TypeKind?: string | null;
    Retired?: boolean;
    RequiredWhen?: any;
    /** The controls this field may be drawn with, derived first. Answered by the catalogue. */
    ControlOptions?: string[];
    /** On a field that offers "Other": which value in its list means it. */
    OtherValueUSID?: string | null;
    /** On a companion field: whose "Other" it captures. */
    OtherForFieldUSID?: string | null;
    USID?: string;
};

export type Shape = {
    RecordTypeRevisionUSID: string;
    DataTypeUSID: string;
    Name: string;
    Code: string;
    Revision?: number;
    Status?: string;
    CanCapture?: boolean;
};

/** A declared field a record binds differently from its shape, as the server reports it. */
export type OwnDefinition = {Code: string; Name: string; RecordType: string | null; ShapeType: string | null; KeepOwn: boolean};

export type DesignResponse = {
    FormRevisionUSID: string;
    /** The LOGICAL form. Object-type links are made against this, so they survive a Revise. */
    FormUSID?: string | null;
    Name?: string;
    Description?: string | null;
    Status?: string;
    Revision?: number;
    Editable: boolean;
    Definition: FormDefinition;
    RecordTypeRevisionUSID?: string | null;
    ShapeFields: ShapeField[];
    /** Record designs only. */
    Differs?: OwnDefinition[];
};

/** A share of the row. A third and a two-thirds sit side by side and fill one.
 *
 *  THE SAME FOUR THE SERVER ACCEPTS. A width it does not know refuses the entire save, so this
 *  list and `WIDTHS` in form_designer.py drifting apart does not produce a rejected field -- it
 *  produces a form that cannot be saved at all, including the save that would fix the width. */
export const WIDTHS: Array<Placement["width"]> = ["full", "half", "third", "two-thirds"];

/** Which controls can be a filter, split by the KIND of filter ticking the box produces.
 *
 *  A SELECT BECOMES A LIST OF ANSWERS; A NUMBER BECOMES TWO BOUNDS. The distinction is not
 *  cosmetic: a value filter is containment over the stored answers, a range is a comparison over
 *  the typed projection, and they are different predicates on the server. The designer needs the
 *  split only to say which one the author is about to create -- "Quality, Regulatory" is not the
 *  same offer as "250 to 5000", and a checkbox that does not say which is a checkbox nobody can
 *  predict.
 *
 *  The same lists the server enforces, named on both sides so the designer never offers a checkbox
 *  whose only outcome is a refusal, and checked on the server because that is where it has to
 *  hold. See cres_t1/service/form_designer.py. */
export const VALUE_FACET_CONTROLS = ["Select", "MultiSelect", "YesNo"];
export const RANGE_FACET_CONTROLS = ["Number", "Date"];
export const FILTERABLE_CONTROLS = [...VALUE_FACET_CONTROLS, ...RANGE_FACET_CONTROLS];

/** What a canvas item is, once placed. Display items have no code, so they are addressed by index. */
export type Selection =
    | {kind: "tab"; key: string}
    | {kind: "field"; code: string}
    | {kind: "display"; index: number}
    | {kind: "section"; key: string}
    | null;
